// Axis scaling for the load test graphs.
//
// Recharts' own auto domain picks awkward bounds and prints raw floats as tick
// labels (e.g. 0.009477489304), and a hard-coded domain clips any test that
// settles further than expected. This builds the domain and ticks ourselves:
// round steps (1, 2, 2.5 or 5 x 10^n), zero always on the axis, and a minimum
// span so the first few thousandths of an inch don't fill the whole chart.

// Smallest deflection range (in) the axis will show.
export const MIN_DEFLECTION_SPAN = 0.25;

export function niceStep(raw) {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = 10 ** exp;
  const f = raw / base;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * base;
}

// Decimal places needed to print every multiple of `step` exactly.
export function stepDecimals(step) {
  let decimals = Math.max(0, Math.ceil(-Math.log10(step) - 1e-9));
  const mantissa = step / 10 ** Math.floor(Math.log10(step));
  if (Math.abs(mantissa - 2.5) < 1e-9) decimals += 1;
  return decimals;
}

/**
 * Domain, ticks and label precision for a value axis that must include zero.
 *
 * @param values     numbers, or anything Number() can read; non-finite ones
 *                   (null, NaN from gaps in the logger data) are ignored
 * @param minSpan    the axis never covers less than this; extra room is added
 *                   below zero, the direction a settling plate moves
 * @param targetTicks roughly how many ticks to aim for
 */
export function zeroAnchoredAxis(values, { minSpan = MIN_DEFLECTION_SPAN, targetTicks = 6 } = {}) {
  let lo = 0;
  let hi = 0;
  for (const v of values || []) {
    const n = typeof v === 'number' ? v : Number(v);
    if (v === null || v === undefined || v === '' || !Number.isFinite(n)) continue;
    if (n < lo) lo = n;
    if (n > hi) hi = n;
  }
  if (hi - lo < minSpan) lo = hi - minSpan;

  const step = niceStep((hi - lo) / (targetTicks - 1));
  const decimals = stepDecimals(step);
  const first = Math.floor(lo / step + 1e-9);
  const last = Math.ceil(hi / step - 1e-9);

  // Readings above zero are usually a few thousandths of rebound or noise. Give
  // them a small margin instead of a whole extra step, which could take a
  // quarter of the plot for 0.01 in.
  const bottom = first * step;
  const span = hi - bottom;
  const top = hi > 0 ? Math.min(last * step, hi + span * 0.05) : 0;

  const ticks = [];
  for (let i = first; i * step <= top + 1e-9; i += 1) {
    ticks.push(Number((i * step).toFixed(decimals)) || 0); // `|| 0` drops -0
  }

  return {
    domain: [ticks[0], top],
    ticks,
    decimals,
    format: (v) => Number(v).toFixed(decimals),
  };
}

/**
 * Domain and ticks that fit a set of values tightly, without forcing zero onto
 * the axis. Used for zoomed views, where the point is to fill the plot with the
 * selected section.
 *
 * @param minSpan the axis never covers less than this, centred on the data, so
 *                a flat stretch isn't blown up into noise
 */
export function fitAxis(values, { minSpan = 0, targetTicks = 6 } = {}) {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of values || []) {
    const n = typeof v === 'number' ? v : Number(v);
    if (v === null || v === undefined || v === '' || !Number.isFinite(n)) continue;
    if (n < lo) lo = n;
    if (n > hi) hi = n;
  }
  if (lo === Infinity) return null;
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  if (hi === lo) {
    lo -= 0.5;
    hi += 0.5;
  }

  const step = niceStep((hi - lo) / (targetTicks - 1));
  const decimals = stepDecimals(step);
  const first = Math.floor(lo / step + 1e-9);
  const last = Math.ceil(hi / step - 1e-9);

  const ticks = [];
  for (let i = first; i <= last; i += 1) {
    ticks.push(Number((i * step).toFixed(decimals)) || 0);
  }

  return {
    domain: [ticks[0], ticks[ticks.length - 1]],
    ticks,
    decimals,
    format: (v) => Number(v).toFixed(decimals),
  };
}
